import { defineStore } from 'pinia'
const storage = window.localStorage
const sessionFlags = window.sessionStorage

// Persisted earpiece volume (0..1) for a WebRTC/BROWSER call's remote-audio
// element (CASH-1092). Unlike muted/mediaReady, volume is a USER PREFERENCE:
// it must survive logout + reload, so it lives in localStorage. Default 1 (full)
// when nothing is saved; clamp defensively so a corrupt value can't escape [0,1].
const readVolume = () => {
  const raw = storage.getItem('WebexVolume')
  const v = raw === null ? 1 : Number(raw)
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1
}

// Foundation (CASH-881) state: connection + station. Call state and agent aux
// codes (CASH-883) layer on top. The live SDK Task object is NOT kept here (it
// has methods and must not be wrapped in a reactive proxy); use/webex.js holds
// it module-side and this store carries only serializable call info for the UI.
export const useWebexStore = defineStore('webex', {
  state: () => ({
    connectionState: 'disconnected', // disconnected | connecting | connected
    agentState: null, // station-login state, set after stationLogin
    // Registered agent id, mirrored from the SDK register() profile (use/webex.js).
    // In-session copy (NOT persisted to storage) so the server force-logout backstop
    // (CASH-1076) can target the agent after the SDK object is gone (tab sleep / dead
    // socket), when the module-level `webex` singleton is no longer available.
    agentId: null,
    stations: [], // available teams/stations from the SDK profile
    station: JSON.parse(storage.getItem('WebexStation') || 'null'), // last selection
    availability: null, // agent state: Available | Idle | null (must be Available to receive contacts)
    // Human-readable Idle reason (e.g. "3-Meeting"), from the server-confirmed
    // agent:stateChange payload's lastStateChangeReason field (see use/webex.js).
    // null when Available or not yet known.
    availabilityReason: null,
    // Active call (CASH-883). null when no contact; otherwise
    // { state: 'offered'|'connected'|'held'|'wrapup', ani, dnis, queue, customerName,
    //   interactionId, outbound, autoWrapupSeconds? } (autoWrapupSeconds set when wrap-up
    //   has an auto timer; outbound true for an agent-placed outdial — CASH-1093)
    call: null,
    // Agent mic muted in the call bridge (CASH-1093). Mirrors task.toggleMute()
    // optimistically (the SDK call returns no state); cleared when the call clears.
    muted: false,
    wrapupCodes: [], // [{ id, name }] from webex.cc.agentConfig (or stable-SDK fallback), for the wrap-up picker
    idleCodes: [], // [{ id, name }] from webex.cc.agentConfig (or stable-SDK fallback), for the Idle picker
    // --- CASH-1092 WebRTC state ---
    volume: readVolume(), // remote-audio earpiece volume, 0..1 (persisted preference)
    loginOptions: [], // string[] of allowed options from the register profile (e.g. ['BROWSER','AGENT_DN'])
    mediaReady: false, // WebRTC media + mic ready → the "connected" indicator
    // Agent dismissed the station-login prompt (or logged out) without logging into a
    // station: stop auto-registering with the CC server on future mounts (WebexConnect's
    // onMounted) until they deliberately reopen the dialog from the nav. sessionStorage
    // (not localStorage) so a brand-new browser session still gets the normal auto-connect.
    autoConnectSuppressed: sessionFlags.getItem('WebexAutoConnectSuppressed') === 'true',
    // Opt-in gate for the auto-reconnect-on-refresh behavior. userStore.getWebexLinked is a
    // durable, account-wide flag (once true for one browser/device, true everywhere that
    // account logs in), so it alone can't tell us this BROWSER SESSION has ever had the agent
    // click the nav to log in. Without this, a brand-new browser for an already-linked agent
    // would silently auto-connect to Webex with no click at all. Set true the moment the nav
    // is clicked (WebexConnect's onClick) so the one-time OAuth round-trip and every refresh
    // after it keep working with no second click; cleared on reset() (full Webex logout) so a
    // refresh after logging out doesn't silently log back in. sessionStorage, same lifetime as
    // autoConnectSuppressed.
    autoConnectArmed: sessionFlags.getItem('WebexAutoConnectArmed') === 'true',
    error: null
  }),
  actions: {
    setConnectionState(state) {
      this.connectionState = state
    },
    setAgentState(state) {
      this.agentState = state
    },
    setAgentId(id) {
      this.agentId = id || null
    },
    setStations(stations) {
      this.stations = stations || []
    },
    setStation(station) {
      this.station = station || null
      station
        ? storage.setItem('WebexStation', JSON.stringify(station))
        : storage.removeItem('WebexStation')
    },
    setAvailability(availability, reason = null) {
      this.availability = availability
      this.availabilityReason = availability === 'Available' ? null : reason
    },
    // Aux codes from webex.cc.agentConfig (or the stable-SDK in-code fallback),
    // populating the Idle / wrap-up pickers.
    setAuxCodes({ wrapupCodes, idleCodes } = {}) {
      this.wrapupCodes = wrapupCodes || []
      this.idleCodes = idleCodes || []
    },
    // Set the remote-audio volume (0..1). The action is the single writer, so it
    // clamps here — no caller can push volume out of range — and persists the
    // preference. (CASH-1092)
    setVolume(v) {
      const clamped = Math.min(1, Math.max(0, Number(v) || 0))
      this.volume = clamped
      storage.setItem('WebexVolume', String(clamped))
    },
    setLoginOptions(opts) {
      this.loginOptions = opts || []
    },
    setMuted(v) {
      this.muted = !!v
    },
    setMediaReady(v) {
      this.mediaReady = !!v
    },
    setCall(call) {
      this.call = call ? { ...call } : null
    },
    // Advance just the lifecycle state of the current call (held / connected / wrapup),
    // leaving the contact fields intact. No-op if there's no active call.
    setCallState(state) {
      if (this.call) this.call = { ...this.call, state }
    },
    clearCall() {
      this.call = null
      this.muted = false
    },
    // Server says the agent is no longer station-logged-in (logout / multi-login /
    // a LoggedOut state event). Clear station + availability so the UI is honest;
    // the SDK websocket itself may still be connected.
    setLoggedOut() {
      this.agentState = null
      this.availability = null
      this.availabilityReason = null
      // Station/media session ended: the WebRTC path is no longer live, so the
      // "connected" indicator and mute must reset. loginOptions is NOT cleared —
      // the SDK is still registered to the same agent/profile. (CASH-1092)
      this.muted = false
      this.mediaReady = false
    },
    setError(error) {
      this.error = error
    },
    setAutoConnectSuppressed(suppressed) {
      this.autoConnectSuppressed = !!suppressed
      suppressed ? sessionFlags.setItem('WebexAutoConnectSuppressed', 'true') : sessionFlags.removeItem('WebexAutoConnectSuppressed')
    },
    setAutoConnectArmed(armed) {
      this.autoConnectArmed = !!armed
      armed ? sessionFlags.setItem('WebexAutoConnectArmed', 'true') : sessionFlags.removeItem('WebexAutoConnectArmed')
    },
    reset() {
      this.connectionState = 'disconnected'
      this.agentState = null
      this.stations = []
      this.availability = null
      this.availabilityReason = null
      this.call = null
      this.muted = false
      this.wrapupCodes = []
      this.idleCodes = []
      this.error = null
      this.agentId = null
      // Full teardown: clear WebRTC session state too. loginOptions IS cleared here
      // (unlike setLoggedOut) because we've deregistered the SDK entirely. volume is
      // intentionally NOT reset — it's a persisted user preference. (CASH-1092)
      this.mediaReady = false
      this.loginOptions = []
      // Full logout (station + SDK deregister): this browser session no longer counts as
      // "logged in", so a refresh must not silently reconnect until the nav is clicked again.
      this.setAutoConnectArmed(false)
    }
  },
  getters: {
    isConnecting: ({ connectionState }) => connectionState === 'connecting',
    isConnected: ({ connectionState }) => connectionState === 'connected',
    isStationLoggedIn: ({ agentState }) => !!agentState,
    isAvailable: ({ availability }) => availability === 'Available',
    getAvailability: ({ availability }) => availability,
    getAvailabilityReason: ({ availabilityReason }) => availabilityReason,
    getStation: ({ station }) => station,
    getStations: ({ stations }) => stations,
    getError: ({ error }) => error,
    getAutoConnectSuppressed: ({ autoConnectSuppressed }) => autoConnectSuppressed,
    getAutoConnectArmed: ({ autoConnectArmed }) => autoConnectArmed,
    getCall: ({ call }) => call,
    hasActiveCall: ({ call }) => !!call,
    isOnHold: ({ call }) => call?.state === 'held',
    isWrapup: ({ call }) => call?.state === 'wrapup',
    isOutbound: ({ call }) => !!call?.outbound,
    isMuted: ({ muted }) => muted,
    getWrapupCodes: ({ wrapupCodes }) => wrapupCodes,
    getIdleCodes: ({ idleCodes }) => idleCodes,
    getAgentId: ({ agentId }) => agentId,
    // Volume (persisted) + whether the active station is a WebRTC/BROWSER login.
    // isBrowserLogin is DERIVED from station.loginOption (single source of truth),
    // never stored separately. Volume/Mute/Answer UI keys off it. (CASH-1092)
    getVolume: ({ volume }) => volume,
    isBrowserLogin: ({ station }) => station?.loginOption === 'BROWSER',
    getLoginOptions: ({ loginOptions }) => loginOptions,
    isMediaReady: ({ mediaReady }) => mediaReady
  }
})
